import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';
import { api } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realIdentity, setRealIdentity] = useState(null);
  const userRef = useRef(null);

  // Keep ref in sync so the auth listener can read current value
  useEffect(() => { userRef.current = user; }, [user]);

  const fetchProfileData = useCallback(async (userId) => {
    if (!userId) {
      setUser(null);
      setTenant(null);
      return;
    }

    try {
      const profilePromise = api.getProfile(userId);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
      );

      const { user: profile, tenant: tenantData } = await Promise.race([profilePromise, timeoutPromise]);

      setUser(profile);
      setTenant(tenantData);
    } catch (err) {
      console.error("AuthContext Profile Fetch Error:", err);
      setUser({ id: userId, role: 'CLERK', name: 'User' });
      setTenant(null);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session?.user) {
          await fetchProfileData(session.user.id);
        }
      } catch (err) {
        console.error("Supabase Session Init Error:", err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // If we already have a user loaded, this is a token refresh — skip re-fetch
        if (userRef.current) return;
        setLoading(true);
        await fetchProfileData(session.user.id);
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setTenant(null);
        setRealIdentity(null);
        setLoading(false);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchProfileData]);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.session?.user) {
        await fetchProfileData(data.session.user.id);
      }
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setTenant(null);
      setRealIdentity(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const enterTenant = async (tenantData) => {
    if (user?.role !== 'SUPER_ADMIN' && !realIdentity) return;
    setRealIdentity({ user, tenant });
    setUser({ ...user, role: 'OWNER' });
    // Fetch fresh tenant data to pick up logo_url and other updates
    try {
      const fresh = await api.getTenantById(tenantData.id);
      setTenant(fresh || tenantData);
    } catch {
      setTenant(tenantData);
    }
  };

  const exitTenant = () => {
    if (!realIdentity) return;
    setUser(realIdentity.user);
    setTenant(realIdentity.tenant);
    setRealIdentity(null);
  };

  const isImpersonating = !!realIdentity;

  return (
    <AuthContext.Provider value={{ user, tenant, setTenant, login, logout, loading, enterTenant, exitTenant, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};